import React, { FunctionComponent, useEffect, useState } from "react";
import { Select, Alert } from "antd";
import { shallowEqual, useSelector } from "react-redux";
import { featureFlags } from "../../../../Common/CurrentFeatureFlags";
import { colorTwoBright } from "../../../../globalStyles/colors/scoutColors";
import {
    IPolygon,
    IChartCompare,
    IChartGenericDatapoint,
    IChartMergedDatapoint,
    IGPSChartMergedDatapoint,
    IGPSData,
    IGCPBigQueryJob,
    IChartVisitorOrigData,
    IChartTAAData,
} from "../../../../Models/Models";
import {
    AnimatedReportCard,
    ReportScrollableSection,
    ReportChartGroup,
    ReportH1,
    ReportSectionHeader,
    ReportSectionNumber,
    formatLabelWithEllipsis,
    sortMergedChartData,
} from "../../../Common";
import RechartTAA from "../../../Common/Charts/RechartTAA";
import { GPSMap } from "./GPSMap";
import { GPSVisitOrig } from "./GPSVisitOrig";
import { IMobilyticsBenchmarkItem } from "../../../../Models/IMobilyticsBenchmarkItem";
import { RenderType } from "../../../../Enums/RenderType";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { isWebGLCompatible } from "../../../../Logic/WebGLLogic";
import { gpsTAAData } from "../../../../Reducers/Selectors/GPSInsightsSelectors";

interface IGPSTradeAreaProps {
    storeData: IGPSData[];
    storePolygon: IPolygon[];
    selectedGpsInsightForReportView: IGCPBigQueryJob[];
    benchmarkItems: IMobilyticsBenchmarkItem[];
    polygons: IPolygon[];
    benchmarkData: IGPSData;
    renderType: RenderType;
}

const webGlCompatible = isWebGLCompatible();

const GPSTradeArea: FunctionComponent<IGPSTradeAreaProps> = (
    props: IGPSTradeAreaProps
) => {
    const {
        storeData,
        storePolygon,
        selectedGpsInsightForReportView,
        polygons,
        benchmarkData,
        benchmarkItems,
        renderType,
    } = props;
    const polygonNames = polygons.map((pol: IPolygon) => pol.name);
    /** state */
    const [visitOrigGeogType, setVisitOrigGeogType] = useState<string>("county");
    const [mergedVisitOrigGPSData, setMergedVisitOrigGPSData]
    = useState<IChartMergedDatapoint[]>(null);
    const [polygonIdentifiers, setPolyIdentifiers] = useState<
    { name: string; identifier: string }[]
  >([]);
    const [polygonDataType, setPolygonDataType] = useState<string>("");
    const [originationData, setOriginationData] = useState<
    Map<string, IChartVisitorOrigData>
  >(new Map());
    const [selectedPolygon, setSelectedPolygon] = useState<string>("");

    const taaData: IChartTAAData[][] = useSelector(gpsTAAData);

    useEffect(() => {
        const polygonIds: { name: string; identifier: string }[] = Array.from(
            polygonNames,
            (n: string, i: number) => ({ name: n, identifier: n.concat(`-${i}`) })
        );

        setPolyIdentifiers(polygonIds);

        const origData = new Map();

        origData.set(polygonIds[0].identifier, storeData[0].visitorOrigination);

        const mergedData = mergeVisitOrigGPSData({
            selection1: origData.get(polygonIds[0].identifier),
            selection2: null,
            national: null,
        });

        setMergedVisitOrigGPSData(sortMergedChartData(mergedData, true));

        if (benchmarkData) {
            origData.set(polygonIds[1].identifier, benchmarkData.visitorOrigination);
        }

        setOriginationData(origData);
        setPolygonDataType(polygonIds[0].identifier);
        setSelectedPolygon(polygonIds[0].name);
    }, []);

    useEffect(() => {
        if (originationData.has(polygonDataType)) {
            setMergedVisitOrigGPSData(
                sortMergedChartData(
                    mergeVisitOrigGPSData({
                        selection1: originationData.get(polygonDataType),
                        selection2: null,
                        national: null,
                    }),
                    true
                )
            );
        }
    }, [visitOrigGeogType, polygonDataType]);

    const mergeVisitOrigGPSData = (
        data: IChartCompare
    ): IChartMergedDatapoint[] => {
        const formattedData: IChartMergedDatapoint[] = data?.selection1
            ? (
          data.selection1[visitOrigGeogType] as Array<IGPSChartMergedDatapoint>
            ).map((item) => {
                return {
                    id: item.id,
                    nationalWeight: null,
                    selection1PrimaryWeight: item.visitorWeight
                        ? item.visitorWeight
                        : item.weight,
                    selection1SecondaryWeight: item.visitWeight,
                    selection2PrimaryWeight: data.selection2
                        ? (data.selection2 as Array<IChartGenericDatapoint>).find(
                            (selection2) => selection2.id === item.id
                        ).weight
                        : null,
                };
            })
            : null;
        return formattedData;
    };

    const handleVisitOrigGeogTypeChange = (str: string) => {
        setVisitOrigGeogType(str);
    };

    const handlePolygonDataChange = (id: string) => {
        const poly = polygonIdentifiers.find((i) => i.identifier == id);

        setPolygonDataType(poly.identifier);
        setSelectedPolygon(poly.name);
    };

    return (
        <ReportScrollableSection id="GPSTradeArea">
            <ReportSectionHeader>
                <ReportSectionNumber>01</ReportSectionNumber>
                <ReportH1>Trade Area</ReportH1>
            </ReportSectionHeader>
            <AnimatedReportCard
                scrollableParentSelector="#GPSReportBody"
                // Disabling the animation, otherwise the google map shows up tiny in the share/print exports
                duration={0}
                animateIn="zoomIn"
                offset={100}
                animateOnce={true}
            >
                {!webGlCompatible ? (
                    <Alert
                        type="warning"
                        message="We can't show the visitor map because WebGL doesn't appear to be working in your browser. Please ensure that your device's video drivers are up to date, and that your browser is up to date and has WebGL support enabled."
                        showIcon={true}
                    />
                ) : (
                    <GPSMap
                        storePolygon={storePolygon}
                        storeData={storeData}
                        benchmarkItems={benchmarkItems}
                        renderType={renderType}
                    />
                )}
                {!taaData.some(d => d === undefined) && (
                    <RechartTAA
                        app={DigitalShareAppType.Mobilytics}
                        itemType={DigitalShareItemType.VisitorTradeArea}
                        renderType={props.renderType}
                        data={taaData}
                        benchmarkItems={props.benchmarkItems}
                    />
                )}

                {!featureFlags.DigitalShare && mergedVisitOrigGPSData && (
                    <ReportChartGroup
                        title="Visit Origination"
                        data={mergedVisitOrigGPSData}
                        tooltipText={`Shows the percent of visits or visitors to ${selectedGpsInsightForReportView[0].title} location by home geography.`}
                        initialYValue="selection1PrimaryWeight"
                        primaryColor={colorTwoBright}
                        primaryWeightLabel="Visitors"
                        secondaryWeightLabel="Visits"
                        scrollableParentSelector="#GPSReportBody"
                        chartAnim="zoomIn"
                        xTickFormatter={formatLabelWithEllipsis}
                        customGroupingDropdowns={[["County", "DMA", "State", "Zipcode"]]}
                        dropdownHandlers={[handleVisitOrigGeogTypeChange]}
                        makeXLabelsFitDemmit={true}
                        xAxisHeight={100}
                        ignoreXTickFormatterOnTooltip={true}
                        sortByValue={true}
                        app={DigitalShareAppType.Mobilytics}
                        itemType={DigitalShareItemType.VisitorOrigination}
                        renderType={props.renderType}
                        customDropdownContent={
                            <>
                                <Select
                                    defaultValue={polygonNames[0]}
                                    value={formatLabelWithEllipsis(selectedPolygon, 12)}
                                    onChange={(value: string) => handlePolygonDataChange(value)}
                                    style={{ marginLeft: "10px" }}
                                >
                                    {polygonIdentifiers.map((n) => (
                                        <Select.Option value={n.identifier} key={n.identifier}>
                                            {formatLabelWithEllipsis(n.name, 12)}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </>
                        }
                    />
                )}
                {featureFlags.DigitalShare && (
                    <GPSVisitOrig
                        storeVisitOrigData={storeData[0].visitorOrigination}
                        benchmarkVisitOrigData={
                            benchmarkData ? benchmarkData.visitorOrigination : null
                        }
                        polygons={polygons}
                        renderType={renderType}
                        benchmarkItems={props.benchmarkItems}
                    />
                )}
            </AnimatedReportCard>
        </ReportScrollableSection>
    );
};

export default GPSTradeArea;
